/**
 * The thread under one document: what the client wrote on the public link, and
 * what we wrote back.
 *
 * One list, both sides, and everything in it is visible to whoever holds the
 * link. That is said under the composer rather than assumed, because the
 * document already has a place for a remark the client must NOT see - its own
 * notes field - and an operator who mixes the two up finds out from the client.
 *
 * Both sides are removable from here, because both sides are published. A line
 * typed into the wrong document, or a test left on a real invoice, is sitting in
 * front of whoever holds the link until somebody takes the row away.
 */
import { useCallback, useEffect, useState } from "react";
import { Loader2, MessageSquare, Send, Trash2 } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import {
  addDocComment, deleteDocComment, listDocComments, type DocComment, type SalesShareType,
} from "../../../lib/sales";
import { Btn, Card, CardHead, fmtDateTime } from "../../ui";
import { ConfirmModal } from "../_shared/ConfirmModal";

export function DocCommentsCard({
  type,
  id,
  canManage,
}: {
  type: SalesShareType;
  id: string;
  canManage: boolean;
}) {
  const t = useT();
  const [rows, setRows] = useState<DocComment[] | null>(null);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  /** The row the dialog is asking about, held whole so the question can quote it. */
  const [removing, setRemoving] = useState<DocComment | null>(null);

  const load = useCallback(() => {
    listDocComments(type, id).then(setRows).catch(() => setRows([]));
  }, [type, id]);
  useEffect(load, [load]);

  const send = async () => {
    const text = body.trim();
    if (!text) return;
    setBusy(true);
    try {
      const added = await addDocComment(type, id, text);
      setRows((prev) => [...(prev ?? []), added]);
      setBody("");
    } finally {
      setBusy(false);
    }
  };

  // Dropped from the list here rather than by reloading it: the delete already
  // answered, and a second round trip only widens the window in which the row
  // the operator just removed is still on their screen.
  const remove = async (c: DocComment) => {
    await deleteDocComment(type, id, c.id);
    setRows((prev) => (prev ?? []).filter((r) => r.id !== c.id));
  };

  return (
    <>
      <Card className="mt-4">
        <CardHead
          title={
            <span className="flex items-center gap-2">
              <MessageSquare className="size-4 opacity-70" />
              {t("sales.comments.title")}
              {rows?.length ? <span className="crm-mut text-[12.5px]">{rows.length}</span> : null}
            </span>
          }
          sub={t("sales.comments.sub")}
        />

        <div className="px-[26px] pb-7 pt-1">
          {rows === null ? (
            <div className="flex justify-center py-4 text-muted-foreground">
              <Loader2 className="size-5 animate-spin" />
            </div>
          ) : rows.length === 0 ? (
            <p className="crm-mut text-[13px] leading-relaxed">{t("sales.comments.empty")}</p>
          ) : (
            <ul className="flex flex-col gap-2.5">
              {rows.map((c) => (
                <li
                  key={c.id}
                  className={`rounded-2xl px-4 py-3 ${c.authorKind === "staff" ? "bg-[var(--crm-sunk)]" : "bg-[var(--crm-sunk)]/50"}`}
                >
                  <div className="flex items-start gap-2">
                    <div className="crm-mut flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 text-[12px]">
                      <span className="font-medium text-foreground">{c.authorName}</span>
                      <span>{t(c.authorKind === "staff" ? "sales.comments.fromUs" : "sales.comments.fromClient")}</span>
                      <span className="crm-tnum">{fmtDateTime(c.createdAt)}</span>
                    </div>
                    {canManage ? (
                      <button
                        type="button"
                        className="crm-cb crm-cb-sm -mr-1 -mt-1"
                        title={t("sales.comments.delete")}
                        aria-label={t("sales.comments.delete")}
                        onClick={() => setRemoving(c)}
                      >
                        <Trash2 className="size-3.5" />
                      </button>
                    ) : null}
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-[13px] leading-relaxed">{c.body}</p>
                </li>
              ))}
            </ul>
          )}

          {canManage ? (
            <div className="mt-4">
              <textarea
                className="crm-area"
                rows={3}
                value={body}
                placeholder={t("sales.comments.placeholder")}
                aria-label={t("sales.comments.title")}
                onChange={(e) => setBody(e.target.value)}
              />
              <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
                <p className="crm-mut min-w-0 flex-1 text-[12.5px] leading-relaxed">{t("sales.comments.visibility")}</p>
                <Btn small tone="dark" onClick={send} disabled={busy || !body.trim()}>
                  {busy ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
                  {t("sales.comments.send")}
                </Btn>
              </div>
            </div>
          ) : null}
        </div>
      </Card>

      {/* Asked rather than done. The row does not go to a bin anywhere, and the
          line the operator is about to lose is the same line the client is
          reading - so the question quotes it back. */}
      {removing ? (
        <ConfirmModal
          title={t("sales.comments.delete")}
          body={t("sales.comments.deleteConfirm", { body: removing.body.slice(0, 120) })}
          confirmLabel={t("common.delete")}
          cancelLabel={t("common.cancel")}
          onConfirm={async () => {
            await remove(removing);
            setRemoving(null);
          }}
          onClose={() => setRemoving(null)}
        />
      ) : null}
    </>
  );
}
